import { Stat, StatGroup, StatLabel, StatNumber } from "@chakra-ui/react";
import { useFeeWaiverConfigRetrieveSuspense } from "gen/endpoints/fee-waiver-config/fee-waiver-config";
import { useFeeWaiverLTDStatsRetrieve } from "gen/endpoints/fee-waiver-ltd-stats/fee-waiver-ltd-stats";
import { useFeeWaiverSummaryTableDataRetrieve } from "gen/endpoints/fee-waiver-summary-table-data/fee-waiver-summary-table-data";
import React from "react";
import { useTranslation } from "react-i18next";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxStats } from "shared/components/table/box/TableBoxStats";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import { DataTable } from "shared/components/table/data/DataTable";
import { tryParseDecimal } from "shared/utils/DecimalUtils";
import { formatMoney } from "shared/utils/MoneyUtils";

export const FeeWaiverSummaryTable: React.FC<{
  fundFamilyId: string;
}> = ({ fundFamilyId }) => {
  const { t } = useTranslation(["fees", "common"]);

  const { data: feeWaiverConfig } =
    useFeeWaiverConfigRetrieveSuspense(fundFamilyId);
  const { data: feeWaiverLTDStats } = useFeeWaiverLTDStatsRetrieve(
    fundFamilyId,
    {
      query: {
        enabled: !!feeWaiverConfig.is_enabled,
      },
    },
  );
  const { data: feeWaiverSummaryTable } = useFeeWaiverSummaryTableDataRetrieve(
    fundFamilyId,
    {
      query: {
        enabled: !!feeWaiverConfig.is_enabled,
      },
    },
  );

  return feeWaiverConfig.is_enabled ? (
    <TableBox isFullWidth={false}>
      <TableBoxHeader>
        <TableBoxTitle>{t("fees:feeWaiver.title")}</TableBoxTitle>
      </TableBoxHeader>
      <TableBoxStats>
        {feeWaiverLTDStats && (
          <StatGroup>
            <Stat size="xs">
              <StatLabel>{t("fees:feeWaiver.cap")}</StatLabel>
              <StatNumber>
                {formatMoney(feeWaiverLTDStats.fee_waiver_cap)}
              </StatNumber>
            </Stat>
            <Stat size="xs">
              <StatLabel>{t("fees:feeWaiver.accrued")}</StatLabel>
              <StatNumber>
                {formatMoney(
                  tryParseDecimal(feeWaiverLTDStats.fee_waiver_amount?.amount)
                    ?.mul(-1)
                    .toString(),
                )}
              </StatNumber>
            </Stat>
            <Stat size="xs">
              <StatLabel>{t("common:remaining")}</StatLabel>
              <StatNumber>
                {formatMoney(feeWaiverLTDStats.remaining_fee_waiver_amount)}
              </StatNumber>
            </Stat>
          </StatGroup>
        )}
      </TableBoxStats>
      <TableBoxBody>
        {feeWaiverSummaryTable && <DataTable data={feeWaiverSummaryTable} />}
      </TableBoxBody>
    </TableBox>
  ) : null;
};
